import { useState, useCallback, useRef, useEffect } from 'react';
import { router, Link, usePage } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Flash messages from the page props.
 */
interface PageProps {
    flash?: {
        success?: string;
        error?: string;
    };
}

/**
 * Book data from the backend.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    hue: number;
    order: number;
    is_published: boolean;
    chapters_count: number;
    pages_count: number;
    cover_image_url: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated books response.
 */
interface PaginatedBooks {
    data: TomeBook[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Books Index page.
 */
interface Props {
    books: PaginatedBooks;
    filters: {
        search: string | null;
        published: string | null;
    };
}

/**
 * GM Tome Books Index page - displays all books with management actions.
 */
export default function Index({ books, filters }: Props) {
    const { flash } = usePage<PageProps>().props;
    const [search, setSearch] = useState(filters.search || '');
    const [publishedFilter, setPublishedFilter] = useState(filters.published || '');
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [orderedBooks, setOrderedBooks] = useState<TomeBook[]>(books.data);
    const [isSaving, setIsSaving] = useState(false);
    const draggedItem = useRef<number | null>(null);
    const dragOverItem = useRef<number | null>(null);

    // Update orderedBooks when books.data changes (after save/redirect)
    useEffect(() => {
        setOrderedBooks(books.data);
    }, [books.data]);

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/tome/books', {
            search: search || undefined,
            published: publishedFilter || undefined,
        }, { preserveState: true });
    }, [search, publishedFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setPublishedFilter('');
        router.get('/gm/tome/books', {}, { preserveState: true });
    };

    // Handle delete
    const handleDelete = (book: TomeBook) => {
        if (confirm(`Are you sure you want to delete "${book.title}"? This will also delete all chapters and pages within it.`)) {
            router.delete(`/gm/tome/books/${book.id}`, { preserveScroll: true });
        }
    };

    // Drag-and-drop handlers
    const handleDragStart = (index: number) => {
        draggedItem.current = index;
    };

    const handleDragEnter = (index: number) => {
        dragOverItem.current = index;
    };

    const handleDragEnd = () => {
        if (draggedItem.current !== null && dragOverItem.current !== null && draggedItem.current !== dragOverItem.current) {
            const newBooks = [...orderedBooks];
            const draggedBook = newBooks[draggedItem.current];
            newBooks.splice(draggedItem.current, 1);
            newBooks.splice(dragOverItem.current, 0, draggedBook);
            setOrderedBooks(newBooks);
        }
        draggedItem.current = null;
        dragOverItem.current = null;
    };

    // Save reordered books
    const handleSaveOrder = async () => {
        setIsSaving(true);
        const booksWithOrder = orderedBooks.map((book, index) => ({
            id: book.id,
            order: index,
        }));

        router.post('/gm/tome/books/reorder', { books: booksWithOrder }, {
            preserveScroll: true,
            onSuccess: () => {
                setIsSaving(false);
                setIsReorderMode(false);
            },
            onError: () => {
                setIsSaving(false);
            },
        });
    };

    // Cancel reorder mode
    const handleCancelReorder = () => {
        setOrderedBooks(books.data);
        setIsReorderMode(false);
    };

    return (
        <GmLayout title="Tome Books" activePath="/gm/tome/books">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white">📚 Tome Books</h1>
                        <p className="text-gray-400 text-sm mt-1">Manage books in the Archivist's Tome</p>
                    </div>
                    <div className="flex gap-3">
                        {isReorderMode ? (
                            <>
                                <button
                                    onClick={handleCancelReorder}
                                    className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                                    disabled={isSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveOrder}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50"
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save Order'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setIsReorderMode(true)}
                                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded transition-colors cursor-pointer"
                                    disabled={books.data.length < 2}
                                    title={books.data.length < 2 ? 'Need at least 2 books to reorder' : 'Reorder books'}
                                >
                                    Reorder
                                </button>
                                <Link
                                    href="/gm/tome"
                                    className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                                >
                                    View Pages
                                </Link>
                                <Link
                                    href="/gm/tome/books/create"
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                                >
                                    + Create Book
                                </Link>
                            </>
                        )}
                    </div>
                </div>

                {/* Flash Messages */}
                {flash?.success && (
                    <div className="mb-6 px-4 py-3 bg-green-900/50 border border-green-700 rounded text-green-300">
                        {flash.success}
                    </div>
                )}
                {flash?.error && (
                    <div className="mb-6 px-4 py-3 bg-red-900/50 border border-red-700 rounded text-red-300">
                        {flash.error}
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Book title or description"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={publishedFilter}
                                onChange={(e) => setPublishedFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All</option>
                                <option value="true">Published</option>
                                <option value="false">Draft</option>
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={handleFilter} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors">Apply</button>
                            <button onClick={handleClearFilters} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors">Clear</button>
                        </div>
                    </div>
                </div>

                {/* Books Grid / Reorder List */}
                {books.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No books found. Create your first book to start organizing the Archivist's Tome.
                    </div>
                ) : isReorderMode ? (
                    <div className="space-y-2">
                        <p className="text-gray-400 text-sm mb-4">
                            Drag and drop books to reorder them. The order will be reflected on the public bookshelf.
                        </p>
                        {orderedBooks.map((book, index) => (
                            <DraggableBookRow
                                key={book.id}
                                book={book}
                                index={index}
                                onDragStart={handleDragStart}
                                onDragEnter={handleDragEnter}
                                onDragEnd={handleDragEnd}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {books.data.map((book) => (
                            <BookCard key={book.id} book={book} onDelete={handleDelete} />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {books.last_page > 1 && <Pagination links={books.links} />}
            </div>
        </GmLayout>
    );
}

/**
 * BookCard - Displays a single book card with actions.
 * The card title and "View" button navigate to the book's show page (pages list).
 * "Preview" opens the public book page in a new tab.
 */
function BookCard({ book, onDelete }: { book: TomeBook; onDelete: (book: TomeBook) => void }) {
    // Generate HSL color from hue for accent styling
    const accentColor = `hsl(${book.hue}, 60%, 40%)`;

    return (
        <div
            className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden hover:border-slate-600 transition-colors"
            style={{ borderTopColor: accentColor, borderTopWidth: '4px' }}
        >
            <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                    {/* Clickable title navigates to book show page */}
                    <Link
                        href={`/gm/tome/books/${book.id}`}
                        className="text-lg font-semibold text-white hover:text-purple-400 transition-colors"
                    >
                        {book.title}
                    </Link>
                    <span className={`px-2 py-1 text-xs rounded ${book.is_published ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}`}>
                        {book.is_published ? 'Published' : 'Draft'}
                    </span>
                </div>
                <p className="text-xs text-gray-500 mb-2">/tome/{book.slug}</p>
                {book.description && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">{book.description}</p>
                )}
                <div className="flex gap-4 text-sm text-gray-400 mb-4">
                    <span>{book.chapters_count} chapters</span>
                    <span>{book.pages_count} pages</span>
                </div>
                <div className="flex gap-2">
                    {/* View button navigates to book show page (pages list) */}
                    <Link
                        href={`/gm/tome/books/${book.id}`}
                        className="flex-1 px-3 py-2 text-center text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                    >
                        View
                    </Link>
                    {/* Edit button navigates to book edit page */}
                    <Link
                        href={`/gm/tome/books/${book.id}/edit`}
                        className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        Edit
                    </Link>
                    {/* Preview opens public book page in new tab */}
                    <a
                        href={`/tome/${book.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        title="Preview public page"
                    >
                        Preview
                    </a>
                    <button
                        onClick={() => onDelete(book)}
                        className="px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * DraggableBookRow - A draggable row for reordering books.
 */
function DraggableBookRow({
    book,
    index,
    onDragStart,
    onDragEnter,
    onDragEnd,
}: {
    book: TomeBook;
    index: number;
    onDragStart: (index: number) => void;
    onDragEnter: (index: number) => void;
    onDragEnd: () => void;
}) {
    const accentColor = `hsl(${book.hue}, 60%, 40%)`;

    return (
        <div
            draggable
            onDragStart={() => onDragStart(index)}
            onDragEnter={() => onDragEnter(index)}
            onDragEnd={onDragEnd}
            onDragOver={(e) => e.preventDefault()}
            className="flex items-center gap-4 p-4 bg-slate-800 border border-slate-700 rounded-lg cursor-grab active:cursor-grabbing hover:border-slate-600 transition-colors"
            style={{ borderLeftColor: accentColor, borderLeftWidth: '4px' }}
        >
            {/* Drag Handle */}
            <div className="text-gray-500 hover:text-gray-300 transition-colors">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
            </div>

            {/* Order Number */}
            <div className="w-8 h-8 flex items-center justify-center bg-slate-700 rounded text-gray-400 text-sm font-mono">
                {index + 1}
            </div>

            {/* Book Info */}
            <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold truncate">{book.title}</h3>
                <p className="text-gray-500 text-sm truncate">
                    {book.chapters_count} chapters • {book.pages_count} pages
                </p>
            </div>

            {/* Status Badge */}
            <span className={`px-2 py-1 text-xs rounded flex-shrink-0 ${book.is_published ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}`}>
                {book.is_published ? 'Published' : 'Draft'}
            </span>
        </div>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}
